import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, XCircle, MinusCircle, ArrowsOutSimple, FilmSlate, Star, Bell, Warning, CaretUp, CaretDown, UploadSimple } from "@phosphor-icons/react";
import {
  getShotAttempts, fileUrl, extendClip, generateVideoFromStill, promoteToLock,
  approveFirstAppearance, rejectFirstAppearance, setRefPriority,
  uploadManualReference, deleteManualReference, manualRetryShot, manualFinalizeShot, approveAttempt,
} from "@/lib/api";
import { toast } from "sonner";

function ChecklistRow({ label, result }) {
  if (!result) return null;
  const passed = result.pass;
  const skipped = result.skipped;
  const Icon = skipped ? MinusCircle : passed ? CheckCircle : XCircle;
  const cls = skipped ? "text-slate-500" : passed ? "text-emerald-400" : "text-rose-500";
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-800 last:border-0">
      <Icon size={16} className={`shrink-0 mt-0.5 ${cls}`} />
      <div className="flex-1">
        <p className="text-xs font-mono uppercase tracking-widest text-slate-400">
          {label} {skipped && <span className="text-slate-600">(skipped)</span>}
        </p>
        <p className="text-sm text-slate-300">{result.notes}</p>
        {result.checks && !skipped && (
          <div className="flex gap-2 mt-1 flex-wrap">
            {Object.entries(result.checks).map(([key, val]) => (
              <span
                key={key}
                data-testid={`fidelity-subcheck-${key}`}
                className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${
                  val ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" : "text-rose-400 border-rose-500/30 bg-rose-500/10"
                }`}
              >
                {key.replaceAll("_", " ")}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AnimateStillControl({ shot, onAnimated }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!prompt.trim()) {
      toast.error("Describe how the still should move");
      return;
    }
    setBusy(true);
    try {
      const updated = await generateVideoFromStill(shot.id, prompt.trim());
      toast.success("Animating to an 8s clip — this runs in the background, refresh in a moment");
      setPrompt("");
      onAnimated(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Animate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        data-testid="animate-still-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="How should this image move?"
        className="bg-slate-950 border-slate-800 rounded-sm text-xs h-8"
      />
      <Button data-testid="animate-still-submit-btn" onClick={submit} disabled={busy} size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-sm shrink-0">
        <FilmSlate size={12} className="mr-1" /> {busy ? "Animating…" : "Animate to Video (8s)"}
      </Button>
    </div>
  );
}

function ExtendClipControl({ shot, onExtended }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const duration = shot.clip_duration_seconds || 0;

  if (duration > 8) {
    return (
      <p data-testid="extend-clip-capped-notice" className="text-[11px] text-slate-600 italic">
        Clip already extended once ({duration}s) — xAI's video-extension API only supports a single
        extension per clip, so a second hop isn't currently supported.
      </p>
    );
  }

  const submit = async () => {
    if (!prompt.trim()) {
      toast.error("Describe how the clip should continue");
      return;
    }
    setBusy(true);
    try {
      const updated = await extendClip(shot.id, prompt.trim(), 8);
      toast.success(`Clip extended to ${updated.clip_duration_seconds}s`);
      setPrompt("");
      onExtended(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        data-testid="extend-clip-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="How should the clip continue?"
        className="bg-slate-950 border-slate-800 rounded-sm text-xs h-8"
      />
      <Button data-testid="extend-clip-submit-btn" onClick={submit} disabled={busy} size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-sm shrink-0">
        <ArrowsOutSimple size={12} className="mr-1" /> {busy ? "Extending…" : `+8s (${duration}s→${duration + 8}s, one-time)`}
      </Button>
    </div>
  );
}

function PromoteButton({ attempt, entityOptions }) {
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState("character");
  const [targetName, setTargetName] = useState("");
  const [busy, setBusy] = useState(false);

  const options = entityOptions[tier] || [];

  const submit = async () => {
    if (!targetName.trim()) {
      toast.error("Pick or type a target entity name");
      return;
    }
    setBusy(true);
    try {
      const createNew = !options.some((o) => o.name === targetName.trim());
      await promoteToLock(attempt.id, { tier, target_name: targetName.trim(), create_new: createNew });
      toast.success(`Promoted to ${tier} lock: ${targetName.trim()}`);
      setOpen(false);
      setTargetName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button data-testid={`promote-to-lock-btn-${attempt.id}`} onClick={() => setOpen(true)} className="text-[10px] font-mono uppercase text-amber-400 hover:text-amber-300 flex items-center gap-1">
        <Star size={10} /> Promote to Lock
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap bg-slate-950/60 border border-amber-500/20 rounded-sm p-2">
      <Select value={tier} onValueChange={(v) => { setTier(v); setTargetName(""); }}>
        <SelectTrigger data-testid={`promote-tier-select-${attempt.id}`} className="h-7 w-28 bg-slate-950 border-slate-800 rounded-sm text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          <SelectItem value="character">Character</SelectItem>
          <SelectItem value="location">Location</SelectItem>
          <SelectItem value="prop">Prop</SelectItem>
        </SelectContent>
      </Select>
      <Input
        data-testid={`promote-target-input-${attempt.id}`}
        value={targetName}
        onChange={(e) => setTargetName(e.target.value)}
        placeholder="Existing or new name"
        list={`promote-options-${attempt.id}`}
        className="h-7 w-40 bg-slate-950 border-slate-800 rounded-sm text-xs"
      />
      <datalist id={`promote-options-${attempt.id}`}>
        {options.map((o) => <option key={o.id} value={o.name} />)}
      </datalist>
      <Button data-testid={`promote-submit-btn-${attempt.id}`} onClick={submit} disabled={busy} size="sm" className="h-7 bg-amber-600 hover:bg-amber-500 rounded-sm text-xs">
        {busy ? "…" : "Promote"}
      </Button>
      <button onClick={() => setOpen(false)} className="text-slate-600 hover:text-slate-300 text-xs">Cancel</button>
    </div>
  );
}

function FirstAppearanceGate({ shot, onResolved }) {
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      const res = await approveFirstAppearance(shot.id);
      toast.success("Approved as lock — dependent shots queued");
      onResolved(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    setBusy(true);
    try {
      const res = await rejectFirstAppearance(shot.id);
      toast.message("Rejected — regenerating this shot");
      onResolved(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="first-appearance-gate" className="border border-cyan-500/30 bg-cyan-500/5 rounded-sm p-3 space-y-2">
      <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 flex items-center gap-1"><Bell size={12} /> First Appearance — Approve as Lock?</p>
      <p className="text-xs text-slate-300">
        This output becomes the LOCKED reference for {(shot.first_appearance_of || []).map((e) => e.name).join(", ")} across
        every later shot that uses it. Owner decision only.
      </p>
      <div className="flex gap-2">
        <Button data-testid="approve-first-appearance-btn" onClick={approve} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-500 rounded-sm">
          <CheckCircle size={12} className="mr-1" /> Approve as Lock
        </Button>
        <Button data-testid="reject-first-appearance-btn" onClick={reject} disabled={busy} size="sm" className="bg-transparent border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 rounded-sm">
          <XCircle size={12} className="mr-1" /> Reject & Regenerate
        </Button>
      </div>
    </div>
  );
}

function RefPriorityPicker({ shot, onResolved }) {
  const buildEntities = () => {
    const seen = new Set();
    const list = [];
    for (const ref of shot.outfit_refs || []) {
      const name = ref.split(":")[0];
      const key = `character:${name}`;
      if (!seen.has(key)) { seen.add(key); list.push({ tier: "character", name }); }
    }
    if (shot.location_ref) list.push({ tier: "location", name: shot.location_ref });
    for (const name of shot.prop_refs || []) list.push({ tier: "prop", name });
    return list;
  };

  const [order, setOrder] = useState(buildEntities);
  const [busy, setBusy] = useState(false);

  const move = (idx, dir) => {
    const next = [...order];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrder(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await setRefPriority(shot.id, order);
      toast.success("Reference priority saved — shot re-queued");
      onResolved(res);
    } catch (e) {
      toast.error("Failed to save priority");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ref-priority-picker" className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-3 space-y-2">
      <p className="font-mono text-[10px] uppercase tracking-widest text-orange-400 flex items-center gap-1">
        <Warning size={12} /> Needs Reference Priority ({order.length} entities, cap is 3)
      </p>
      <p className="text-xs text-slate-300">Rank which entities win a reference image slot — top 3 get sent, the rest generate from text description only.</p>
      <div className="space-y-1">
        {order.map((e, idx) => (
          <div key={`${e.tier}-${e.name}`} data-testid={`ref-priority-item-${idx}`} className="flex items-center justify-between bg-slate-950/60 border border-slate-800 rounded-sm px-2 py-1">
            <span className="text-xs text-slate-300">
              <span className="text-slate-600 font-mono">{idx + 1}.</span> {e.tier}: {e.name}{" "}
              {idx < 3 && <span className="text-emerald-400 text-[10px]">(sent)</span>}
            </span>
            <div className="flex gap-1">
              <button data-testid={`ref-priority-up-${idx}`} onClick={() => move(idx, -1)} className="text-slate-600 hover:text-slate-200"><CaretUp size={12} /></button>
              <button data-testid={`ref-priority-down-${idx}`} onClick={() => move(idx, 1)} className="text-slate-600 hover:text-slate-200"><CaretDown size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      <Button data-testid="save-ref-priority-btn" onClick={save} disabled={busy} size="sm" className="bg-orange-600 hover:bg-orange-500 rounded-sm">
        {busy ? "Saving…" : "Save Priority & Re-queue"}
      </Button>
    </div>
  );
}

function ManualReviewPanel({ shot, onResolved }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [finalizing, setFinalizing] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const updated = await uploadManualReference(shot.id, file);
      toast.success("Manual reference uploaded");
      onResolved(updated);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (path) => {
    const updated = await deleteManualReference(shot.id, path);
    onResolved(updated);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await manualRetryShot(shot.id);
      toast.success("Manual retry started — your uploaded reference(s) get top priority");
      onResolved({ ...shot, gen_status: "generating" });
    } catch {
      toast.error("Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const handleFinalize = async (path) => {
    setFinalizing(path);
    try {
      const updated = await manualFinalizeShot(shot.id, path);
      toast.success("Set as this shot's final output");
      onResolved(updated);
    } catch {
      toast.error("Finalize failed");
    } finally {
      setFinalizing(null);
    }
  };

  const refs = shot.manual_reference_paths || [];

  return (
    <div data-testid="manual-review-panel" className="border border-rose-500/30 bg-rose-500/5 rounded-sm p-3 space-y-2">
      <p className="font-mono text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1">
        <Warning size={12} /> Manual Review — Failed 3 Automatic Attempts
      </p>
      <p className="text-xs text-slate-300">
        Upload a corrected reference, then either retry generation using it as the top-priority reference, or use it directly as this shot's final output.
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {refs.map((p) => (
          <div key={p} className="relative group">
            <img src={fileUrl(p)} alt="manual reference" className="w-full h-16 object-cover rounded-sm border border-slate-800" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-slate-950/80 opacity-0 group-hover:opacity-100 transition-opacity">
              <button data-testid={`use-as-final-btn-${shot.id}-${p}`} onClick={() => handleFinalize(p)} disabled={!!finalizing} className="text-[9px] font-mono uppercase text-emerald-400 hover:text-emerald-300">
                {finalizing === p ? "…" : "Use as Final"}
              </button>
              <button data-testid={`remove-manual-ref-btn-${shot.id}-${p}`} onClick={() => handleRemove(p)} className="text-[9px] font-mono uppercase text-rose-400 hover:text-rose-300">
                Remove
              </button>
            </div>
          </div>
        ))}
        <button data-testid={`upload-manual-ref-btn-${shot.id}`} onClick={() => inputRef.current.click()} disabled={uploading} className="w-full h-16 flex items-center justify-center border border-dashed border-slate-700 rounded-sm text-slate-500 hover:text-rose-400 hover:border-rose-500/50 transition-colors">
          <UploadSimple size={16} />
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
      {refs.length > 0 && (
        <Button data-testid={`manual-retry-btn-${shot.id}`} onClick={handleRetry} disabled={retrying} size="sm" className="bg-rose-600 hover:bg-rose-500 rounded-sm">
          {retrying ? "Retrying…" : "Retry Generation With These References"}
        </Button>
      )}
    </div>
  );
}

function ApproveResultButton({ shot, attempt, onApproved }) {
  const [busy, setBusy] = useState(false);
  const isActive = shot.latest_attempt_id === attempt.id;

  if (isActive) {
    return (
      <span data-testid={`current-result-badge-${attempt.id}`} className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
        <CheckCircle size={11} weight="fill" /> Current Approved Result
      </span>
    );
  }

  const approve = async () => {
    setBusy(true);
    try {
      const updated = await approveAttempt(shot.id, attempt.id);
      toast.success(`Attempt ${attempt.attempt_number} approved as this shot's final result`);
      onApproved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button data-testid={`approve-attempt-btn-${attempt.id}`} onClick={approve} disabled={busy} size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs">
      <CheckCircle size={12} className="mr-1" /> {busy ? "Approving…" : "Approve This Result"}
    </Button>
  );
}

function AnimateResultControl({ shot, attempt, onApproved }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (attempt.video_path || !attempt.image_path) return null;

  const submit = async () => {
    if (!prompt.trim()) {
      toast.error("Describe how the still should move");
      return;
    }
    setBusy(true);
    try {
      // Approving first makes this attempt's image the shot's active still — the Warden's verdict
      // on it is untouched, this only overrides which result the shot currently points to.
      await approveAttempt(shot.id, attempt.id);
      const updated = await generateVideoFromStill(shot.id, prompt.trim());
      toast.success("Animating to an 8s clip — this runs in the background, refresh in a moment");
      setPrompt("");
      setExpanded(false);
      onApproved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Animate failed");
    } finally {
      setBusy(false);
    }
  };

  if (!expanded) {
    return (
      <button data-testid={`animate-attempt-btn-${attempt.id}`} onClick={() => setExpanded(true)} className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-fuchsia-400 hover:text-fuchsia-300 transition-colors">
        <FilmSlate size={11} /> Animate This Result
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Input
        data-testid={`animate-attempt-prompt-${attempt.id}`}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="How should this image move?"
        className="bg-slate-950 border-slate-800 rounded-sm text-xs h-7 w-48"
      />
      <Button data-testid={`animate-attempt-submit-${attempt.id}`} onClick={submit} disabled={busy} size="sm" className="h-7 bg-fuchsia-600 hover:bg-fuchsia-500 rounded-sm text-xs">
        {busy ? "…" : "Animate (8s)"}
      </Button>
      <button onClick={() => setExpanded(false)} className="text-slate-600 hover:text-slate-300 text-xs">
        Cancel
      </button>
    </div>
  );
}

export default function ShotAttemptDialog({ shot, open, onOpenChange, onShotUpdated, entityOptions }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && shot) {
      setLoading(true);
      getShotAttempts(shot.id)
        .then((a) => setAttempts(a.reverse()))
        .finally(() => setLoading(false));
    }
  }, [open, shot]);

  if (!shot) return null;
  const shotCode = `S${String(shot.scene_number).padStart(2, "0")}_SH${String(shot.shot_number).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shot-attempt-dialog" className="bg-slate-900 border-slate-800 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            {shotCode} — Generation Attempts
          </DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {shot.gen_status === "pending_approval" && <FirstAppearanceGate shot={shot} onResolved={onShotUpdated} />}
        {shot.gen_status === "needs_ref_priority" && <RefPriorityPicker shot={shot} onResolved={onShotUpdated} />}
        {shot.gen_status === "failed_red" && <ManualReviewPanel shot={shot} onResolved={onShotUpdated} />}
        {shot.gen_status === "passed" && !shot.latest_video_path && shot.latest_image_path && (
          <div data-testid="animate-still-section" className="border border-fuchsia-500/20 bg-fuchsia-500/5 rounded-sm p-3 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400">Animate This Approved Still to Video (8s)</p>
            <AnimateStillControl shot={shot} onAnimated={onShotUpdated} />
          </div>
        )}
        {shot.output_type === "clip" && shot.gen_status === "passed" && shot.latest_video_path && (
          <div data-testid="extend-clip-section" className="border border-fuchsia-500/20 bg-fuchsia-500/5 rounded-sm p-3 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400">Extend Clip (one-time, 8s → up to 16s)</p>
            <ExtendClipControl shot={shot} onExtended={onShotUpdated} />
          </div>
        )}
        <div className="space-y-6">
          {attempts.map((a) => (
            <div key={a.id} data-testid={`attempt-block-${a.id}`} className="border border-slate-800 rounded-sm p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-widest text-blue-400">
                  Attempt {a.attempt_number} · {a.provider}
                </span>
                <span className="font-mono text-xs text-slate-500">${a.cost.toFixed(3)}</span>
              </div>
              {a.video_path ? (
                <video src={fileUrl(a.video_path)} controls className="w-full max-h-80 rounded-sm border border-slate-800 bg-black" />
              ) : (
                <img src={fileUrl(a.image_path)} alt={`attempt ${a.attempt_number}`} className="w-full max-h-80 object-contain rounded-sm border border-slate-800 bg-black" />
              )}
              <div>
                <ChecklistRow label="Character Fidelity" result={a.warden.fidelity} />
                <ChecklistRow label="Intent Match" result={a.warden.intent} />
                <ChecklistRow label="Technical" result={a.warden.technical} />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Verdict: <span className="text-slate-300">{a.warden.verdict}</span>
                </p>
                {a.warden.verdict === "pass" && entityOptions && <PromoteButton attempt={a} entityOptions={entityOptions} />}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 border-t border-slate-800 pt-2">
                <ApproveResultButton shot={shot} attempt={a} onApproved={onShotUpdated} />
                <AnimateResultControl shot={shot} attempt={a} onApproved={onShotUpdated} />
              </div>
            </div>
          ))}
          {!loading && attempts.length === 0 && <p className="text-sm text-slate-500">No generation attempts yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
